import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Badge,
  Card,
  ConfirmModal,
  Icon,
  Menu,
  Modal,
  Page,
  SimpleSelect,
  Skeleton,
  TextInput,
} from 'corso-admin-ui';
import { CrewMerchantUi, RoleCode, roleCodeName } from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import EmptyStateAction from '~/components/EmptyStateAction';
import { useStoreUser } from '~/providers/StoreUserProvider';
import { useUser, useUserOrgs } from '~/providers/UserProvider';
import { StoreUser, UpsertFormProps } from '~/types';
import {
  useResetUserPassword,
  useStoreUserDelete,
  useStoreUsers,
  useStoreUserUpsert,
} from './hooks/useStoreUsers';

const { storeUserCreateSchema, storeUserUpdateSchema } = CrewMerchantUi;

const roleCodeOptions = [
  {
    value: RoleCode.staff,
    label: roleCodeName[RoleCode.staff],
  },
  {
    value: RoleCode.admin,
    label: roleCodeName[RoleCode.admin],
  },
];

function UserForm({
  show,
  onClose,
  onSubmit,
  values,
}: UpsertFormProps<
  z.infer<typeof storeUserCreateSchema>,
  z.infer<typeof storeUserUpdateSchema>
>) {
  const {
    store: { id: storeId },
  } = useStoreUser();
  const update = !!values?.id;

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm({
    resolver: zodResolver(storeUserUpdateSchema.or(storeUserCreateSchema)),
    values,
    defaultValues: {
      storeUserRole: {
        storeId,
      },
    },
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((formValues) => onSubmit(formValues))(event)
      .then(() => reset())
      .catch(console.error);
  };

  return (
    <Modal
      title={update ? 'Edit User' : 'Invite User'}
      description={
        update
          ? 'Admins have full access to all settings, while staff can only view and edit claims.'
          : 'Create a new user for your store, and assign them a role. Admins have full access to all settings, while staff can only view and edit claims.'
      }
      show={show}
      onClose={onClose}
    >
      <form className="flex flex-col gap-4" onSubmit={submitHandler}>
        <TextInput
          id="firstName"
          label="First Name"
          required
          {...register('firstName')}
          error={errors.firstName?.message}
        />

        <TextInput
          id="lastName"
          label="Last Name"
          required
          {...register('lastName')}
          error={errors.lastName?.message}
        />

        {!update && (
          <TextInput
            id="email"
            label="Email"
            required
            {...register('email')}
            error={errors.email?.message}
          />
        )}

        <Controller
          name="storeUserRole.roleCode"
          control={control}
          render={({ field: { onChange, value } }) => (
            <SimpleSelect
              label="Role"
              placeholder="Select a role"
              options={roleCodeOptions}
              value={value}
              onChange={onChange}
              error={errors.storeUserRole?.roleCode?.message}
            />
          )}
        />
        <Action variant="primary" type="submit">
          {update ? 'Save' : 'Send Invite'}
        </Action>
      </form>
    </Modal>
  );
}

function EditUser({
  user,
  show,
  onClose,
}: {
  user: StoreUser;
  show: boolean;
  onClose: () => void;
}) {
  const { mutate: upsertUser } = useStoreUserUpsert();

  return (
    <UserForm
      values={user}
      show={show}
      onClose={onClose}
      onSubmit={(upsert) => {
        upsertUser(upsert);
        onClose();
      }}
    />
  );
}

function DeleteUser({
  user,
  show,
  onClose,
}: {
  user: StoreUser;
  show: boolean;
  onClose: () => void;
}) {
  const { mutate: deleteUser, isPending } = useStoreUserDelete();
  const confirmDelete = () => {
    deleteUser(user);
    onClose();
  };

  return (
    <ConfirmModal
      title="Delete User"
      prompt={`Are you sure you want to delete ${user.email}? This will immediately revoke access to Crew.`}
      confirmText="Delete"
      cancelText="Cancel"
      show={show}
      loading={isPending}
      onConfirm={confirmDelete}
      onCancel={onClose}
    />
  );
}

function UserDisplay({ user }: { user: StoreUser }) {
  const { email, storeUserRole, firstName, lastName } = user;
  const { id: loggedInUserId } = useUser();
  const userOrgs = useUserOrgs();
  const hasOrgMembership = userOrgs.some((o) =>
    o.storeIds.includes(user.storeUserRole.storeId),
  );

  const { mutate: resetPassword } = useResetUserPassword();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const closeDeleteConfirmation = () => setShowDeleteConfirmation(false);

  const [showForm, setShowForm] = useState(false);
  const closeForm = () => {
    setShowForm(false);
  };

  const name = `${firstName} ${lastName}`.trim();

  if (storeUserRole.roleCode === RoleCode.corsoAdmin) return null; // we shouldn't ever get users to list with a Corso Admin role, but skip them just in case // TODO adjust the API response to affirm this, so this can be removed

  return (
    <li className="group grid grid-cols-[auto_1fr_auto] items-center justify-between gap-4 px-2 py-3 odd:bg-white even:bg-neutral-50 sm:flex-row sm:px-4">
      <Icon icon="customer" className="size-10 text-neutral-400/80" />
      <div>
        <div className="flex flex-wrap gap-2">
          <p className="text-sm leading-6 font-semibold text-neutral-800">
            {name}
          </p>
          <Badge>{roleCodeName[storeUserRole.roleCode]}</Badge>
          {userOrgs.map((o) => (
            <Badge key={o.organizationId} variant="info">
              {o.organizationName}
            </Badge>
          ))}
        </div>
        <a
          href={`mailto:${email}`}
          className="mt-1 flex truncate text-xs leading-5 text-neutral-500 hover:underline"
        >
          {email}
        </a>
      </div>
      {!hasOrgMembership && (
        <>
          <Menu
            align="end"
            buttonAs={
              <Action
                icon="overflowMenu"
                accessibilityLabel={`Actions for ${name}`}
              />
            }
          >
            <Menu.ItemButton onClick={() => setShowForm(true)}>
              <Icon icon="edit" /> Edit User
            </Menu.ItemButton>
            <Menu.ItemButton onClick={() => resetPassword(user)}>
              <Icon icon="lock" className="size-4" /> Reset Password
            </Menu.ItemButton>
            {user.id !== loggedInUserId && (
              <Menu.ItemButton onClick={() => setShowDeleteConfirmation(true)}>
                <span className="text-red-600">
                  <Icon icon="remove" /> Delete User
                </span>
              </Menu.ItemButton>
            )}
          </Menu>
          <EditUser user={user} show={showForm} onClose={closeForm} />
          <DeleteUser
            user={user}
            show={showDeleteConfirmation}
            onClose={closeDeleteConfirmation}
          />
        </>
      )}
    </li>
  );
}

function UserSkeleton() {
  return (
    <div className="flex flex-col justify-between gap-4 px-4 py-5 sm:flex-row sm:px-6">
      <div className="flex gap-x-2">
        <Skeleton.Circle diameter="36px" />
        <Skeleton.Rectangle height="49px" width="251px" />
      </div>
      <div className="flex flex-row items-center gap-2">
        <Skeleton.Rectangle width="44px" height="36px" />
        <Skeleton.Rectangle width="132px" height="36px" />
        <Skeleton.Rectangle width="44px" height="36px" />
      </div>
    </div>
  );
}

export default function UserSettingsPage() {
  const { data: users = [], isPending } = useStoreUsers();
  const [showForm, setShowForm] = useState(false);
  const { mutate: inviteUser } = useStoreUserUpsert();

  return (
    <Page
      title="Users"
      primaryAction={{
        onAction: () => setShowForm(true),
        icon: 'add',
        content: 'Invite User',
      }}
    >
      {isPending ? (
        <Card>
          <Skeleton
            instances={3}
            skeleton={UserSkeleton}
            isLoading={isPending}
          />
        </Card>
      ) : (
        <Card>
          {!users.length ? (
            <EmptyStateAction.Button
              onClick={() => setShowForm(true)}
              icon={<Icon icon="users" />}
              label="Invite Users"
              description="Invite users to your store to help manage your claims."
            />
          ) : (
            <ul className="flex flex-col">
              {users.map((user) => (
                <UserDisplay key={user.id} user={user} />
              ))}
            </ul>
          )}
        </Card>
      )}

      <UserForm
        show={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={(user) => {
          inviteUser(user);
          setShowForm(false);
        }}
      />
    </Page>
  );
}
